import { useState, useEffect, lazy, Suspense } from 'react';
import { Home, Calendar, Users, Mail, CalendarDays } from 'lucide-react';
import { NavBar } from './components/ui/tubelight-navbar';
import MobileNavWrapper from './components/MobileNavWrapper';
import Footer from './components/Footer';
import ScrollManager from './components/ScrollManager';
import BookingSuccessToast from './components/BookingSuccessToast';
import PageTransition from './components/PageTransition';
import { EnvironmentBadge } from './components/admin/EnvironmentBadge';
import HomePage from './pages/HomePage';
import BookingPage from './pages/BookingPage';
import MembershipPage from './pages/MembershipPage';
import ContactPage from './pages/ContactPage';
import ImpressumPage from './pages/ImpressumPage';
import DatenschutzPage from './pages/DatenschutzPage';
import BuchungsbedingungenPage from './pages/BuchungsbedingungenPage';
import CookieEinstellungenPage from './pages/CookieEinstellungenPage';
import PayPalReturn from './pages/paypal/return';
import PayPalCancel from './pages/paypal/cancel';
import SuccessPage from './pages/SuccessPage';
import ErrorPage from './pages/ErrorPage';
import CancelFreePage from './pages/CancelFreePage';
import UpcomingEventsPage from './pages/UpcomingEventsPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
/*
  The private admin surfaces load on demand.

  Statically importing them put the whole Control Center — thirteen sections,
  their tables, charts and PDF machinery — into the entry chunk that every
  PUBLIC visitor downloads before they can book a court. Nobody arriving at the
  booking page needs the reconciliation screen. Splitting here keeps the public
  critical path free of admin code, and an administrator pays for the dashboard
  once, at the moment they open it.
*/
const AdminMitgliederPage = lazy(() => import('./pages/AdminMitgliederPage'));
const AdminControlCenter = lazy(() => import('./pages/AdminControlCenter'));
// DEV-ONLY: lazily referenced so the production build can drop it entirely.
const AdminHarness = import.meta.env.DEV
  ? lazy(() => import('./dev/adminHarness/AdminHarness'))
  : () => null;
import GutscheinSuccessPage from './pages/GutscheinSuccessPage';
import BookingSuccessPage from './pages/BookingSuccessPage';
import BookingSlotUnavailablePage from './pages/BookingSlotUnavailablePage';
import GutscheinPaymentSuccessPage from './pages/GutscheinPaymentSuccessPage';
import ContactSuccessPage from './pages/ContactSuccessPage';
import MembershipSuccessPage from './pages/MembershipSuccessPage';
import CancelBookingPage from './pages/CancelBookingPage';
import CancelDonePage from './pages/CancelDonePage';
import CancelSuccessPage from './pages/CancelSuccessPage';
import CancelStripeBookingPage from './pages/CancelStripeBookingPage';
import CancelPaypalBookingPage from './pages/CancelPaypalBookingPage';

/**
 * Shown only while the admin chunk is in flight.
 *
 * Deliberately the same warm canvas and the same indeterminate rail the
 * Control Center's own boot screen uses, so a slow network reads as one
 * continuous start-up rather than as a blank page followed by a different
 * loading screen. It carries no logo of its own: the shell's boot screen
 * takes over the moment the chunk lands, and two brand marks appearing in
 * sequence would look like a stutter.
 */
function AdminChunkFallback() {
  return (
    <div
      data-admin-theme="light"
      className="adm-root min-h-screen flex items-center justify-center p-6"
      role="status"
      aria-live="polite"
    >
      <div className="w-full max-w-[240px]">
        <div className="adm-progress w-full" />
        <p className="text-[11px] mt-3.5 text-center text-[#9b968e]">Control Center wird geladen…</p>
      </div>
    </div>
  );
}

type Page = 'home' | 'booking' | 'events' | 'membership' | 'contact' | 'impressum' | 'datenschutz' | 'buchungsbedingungen' | 'cookie-einstellungen' | 'paypal-return' | 'paypal-cancel' | 'success' | 'error' | 'cancel-free' | 'payment-success' | 'payment-cancel' | 'admin-mitglieder' | 'admin-center' | 'gutschein-success' | 'booking-success' | 'success-gutschein' | 'contact-success' | 'membership-success' | 'cancel-booking' | 'cancel-done' | 'cancel-success' | 'cancel-stripe' | 'cancel-paypal' | 'booking-slot-unavailable' | 'admin-harness';

function App() {
  const navItems = [
    { name: 'Home', page: 'home' as const, icon: Home },
    { name: 'Buchen', page: 'booking' as const, icon: Calendar },
    { name: 'Events & Gutschein', page: 'events' as const, icon: CalendarDays },
    { name: 'Mitgliedschaft', page: 'membership' as const, icon: Users },
    { name: 'Kontakt', page: 'contact' as const, icon: Mail }
  ];


  const getInitialPage = (): Page => {
    const path = window.location.pathname;
    if (path === '/paypal/return') return 'paypal-return';
    if (path === '/paypal/cancel') return 'paypal-cancel';
    if (path === '/payment/success') return 'payment-success';
    if (path === '/payment/cancel') return 'payment-cancel';
    if (path === '/gutschein/success' || path === '/gutschein-success') return 'gutschein-success';
    if (path === '/booking/success') return 'booking-success';
    if (path === '/booking/slot-unavailable') return 'booking-slot-unavailable';
    if (path === '/cancel-free') return 'cancel-free';
    if (path === '/admin/mitglieder') return 'admin-mitglieder';
    if (path === '/admin/center' || path === '/admin') return 'admin-center';
    // DEV-ONLY visual harness. `import.meta.env.DEV` is statically replaced
    // with `false` at build time, so this branch and the module it lazy-loads
    // are dropped from the production bundle.
    if (import.meta.env.DEV && path === '/admin/__harness') return 'admin-harness';
    if (path === '/success') return 'success';
    if (path === '/error') return 'error';
    if (path === '/success-gutschein') return 'success-gutschein';
    if (path === '/contact/success') return 'contact-success';
    if (path === '/membership/success') return 'membership-success';
    if (path === '/cancel/success') return 'cancel-success';
    if (path === '/cancel/done') return 'cancel-done';
    if (path === '/cancel-stripe') return 'cancel-stripe';
    if (path === '/cancel/paypal') return 'cancel-paypal';
    if (path === '/cancel') return 'cancel-booking';
    return 'home';
  };

  const [currentPage, setCurrentPage] = useState<Page>(getInitialPage());

  /*
    The page components declare `onNavigate?: (page: string) => void`, which is
    WIDER than `Page`, so passing `setCurrentPage` directly is a contravariance
    error at every call site. Narrowing here in one place is the smaller change
    than editing each component's props, and every caller passes a literal from
    the same union — HomePage even types its own handler as `(p: Page)`.
  */
  const navigate = (page: string) => setCurrentPage(page as Page);


  useEffect(() => {
    const handlePopState = () => {
      setCurrentPage(getInitialPage());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <HomePage onNavigate={navigate} />;
      case 'booking':
        return <BookingPage onNavigate={navigate} />;
      case 'events':
        return <UpcomingEventsPage onNavigate={navigate} />;
      case 'membership':
        return <MembershipPage onNavigate={navigate} />;
      case 'contact':
        return <ContactPage />;
      case 'impressum':
        return <ImpressumPage />;
      case 'datenschutz':
        return <DatenschutzPage />;
      case 'buchungsbedingungen':
        return <BuchungsbedingungenPage />;
      case 'cookie-einstellungen':
        return <CookieEinstellungenPage />;
      case 'paypal-return':
        return <PayPalReturn />;
      case 'paypal-cancel':
        return <PayPalCancel />;
      case 'payment-success':
        return <PaymentSuccessPage />;
      case 'payment-cancel':
        return <PaymentCancelPage />;
      case 'gutschein-success':
        return <GutscheinSuccessPage />;
      case 'booking-success':
        return <BookingSuccessPage />;
      case 'booking-slot-unavailable':
        return <BookingSlotUnavailablePage />;
      case 'success':
        return <SuccessPage onNavigate={navigate} />;
      case 'error':
        return <ErrorPage />;
      case 'cancel-free':
        return <CancelFreePage />;
      case 'admin-mitglieder':
        return <Suspense fallback={<AdminChunkFallback />}><AdminMitgliederPage /></Suspense>;
      case 'admin-center':
        return <Suspense fallback={<AdminChunkFallback />}><AdminControlCenter /></Suspense>;
      case 'admin-harness':
        return import.meta.env.DEV
          ? <Suspense fallback={<AdminChunkFallback />}><AdminHarness /></Suspense>
          : <ErrorPage />;
      case 'success-gutschein':
        return <GutscheinPaymentSuccessPage />;
      case 'contact-success':
        return <ContactSuccessPage />;
      case 'membership-success':
        return <MembershipSuccessPage />;
      case 'cancel-booking':
        return <CancelBookingPage />;
      case 'cancel-done':
        return <CancelDonePage />;
      case 'cancel-success':
        return <CancelSuccessPage />;
      case 'cancel-stripe':
        return <CancelStripeBookingPage />;
      case 'cancel-paypal':
        return <CancelPaypalBookingPage />;
      default:
        return <HomePage onNavigate={navigate} />;
    }
  };

  const isFullScreenPage = currentPage === 'success' || currentPage === 'error' || currentPage === 'payment-success' || currentPage === 'payment-cancel' || currentPage === 'booking-success' || currentPage === 'booking-slot-unavailable' || currentPage === 'gutschein-success' || currentPage === 'paypal-return' || currentPage === 'paypal-cancel' || currentPage === 'cancel-free' || currentPage === 'success-gutschein' || currentPage === 'contact-success' || currentPage === 'membership-success' || currentPage === 'cancel-booking' || currentPage === 'cancel-done' || currentPage === 'cancel-success' || currentPage === 'cancel-stripe' || currentPage === 'cancel-paypal' || currentPage === 'admin-mitglieder' || currentPage === 'admin-center' || currentPage === 'admin-harness';

  // Non-production builds tell the administrator which backend they are on.
  // Renders nothing in the production deploy context. See lib/backendEnv.ts.
  const environmentBadge = currentPage.startsWith('admin-') ? <EnvironmentBadge /> : null;

  if (isFullScreenPage) {
    return (
      <>
        {environmentBadge}
        <PageTransition key={currentPage}>
          {renderPage()}
        </PageTransition>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {environmentBadge}
      <ScrollManager currentPage={currentPage} />
      <BookingSuccessToast />
      <NavBar items={navItems} currentPage={currentPage} onNavigate={setCurrentPage} />
      <main className="pt-20 pb-28 md:pb-8 sm:pt-24">
        <PageTransition key={currentPage}>
          {renderPage()}
        </PageTransition>
      </main>

      {/* Mobile Navigation Menu - Only visible on mobile */}
      <div className="mobile-nav-portal md:hidden">
        <MobileNavWrapper
          currentPage={currentPage as 'home' | 'booking' | 'events' | 'membership' | 'contact'}
          onNavigate={setCurrentPage}
        />
      </div>

      <Footer onNavigate={setCurrentPage} />
    </div>
  );
}

export default App;